import React, { useState, useEffect } from 'react';
import './InstallPrompt.css';

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSSteps, setShowIOSSteps] = useState(false);
  const [isFacebookBrowser, setIsFacebookBrowser] = useState(false);

  useEffect(() => {
    const ua = navigator.userAgent || navigator.vendor || window.opera;

    const ios = /iphone|ipad|ipod/i.test(ua);

    // Detect Facebook and Instagram in-app browsers
    const facebookBrowser = /FBAN|FBAV|Instagram/i.test(ua);

    const installed =
      window.matchMedia('(display-mode: standalone)').matches ||
      navigator.standalone;

    setIsIOS(ios);
    setIsFacebookBrowser(facebookBrowser);

    if (installed) return;

    const dismissed = localStorage.getItem('install_prompt_dismissed');

    if (
      dismissed &&
      (Date.now() - parseInt(dismissed, 10)) / 86400000 < 7
    ) {
      return;
    }

    /*
     * Facebook/Instagram browsers usually don't support
     * beforeinstallprompt properly.
     *
     * Instead, show a message telling the user to open
     * Godly Match in Chrome/Samsung Internet.
     */
    if (facebookBrowser) {
      setTimeout(() => {
        setShowPrompt(true);
      }, 3000);

      return;
    }

    const handler = (e) => {
      console.log('beforeinstallprompt event fired');

      e.preventDefault();

      setDeferredPrompt(e);

      setTimeout(() => {
        setShowPrompt(true);
      }, 3000);
    };

    window.addEventListener('beforeinstallprompt', handler);

    const handleInstalled = () => {
      console.log('PWA was installed');
      setShowPrompt(false);
    };

    window.addEventListener('appinstalled', handleInstalled);

    if (ios) {
      setTimeout(() => setShowPrompt(true), 3000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', handleInstalled);
    };
  }, []);

  const handleInstall = async () => {
    // Facebook / Instagram browser
    if (isFacebookBrowser) {
      return;
    }

    // iPhone / iPad
    if (isIOS) {
      setShowIOSSteps(true);
      return;
    }

    if (!deferredPrompt) return;

    deferredPrompt.prompt();

    await deferredPrompt.userChoice;

    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setShowIOSSteps(false);

    localStorage.setItem(
      'install_prompt_dismissed',
      Date.now().toString()
    );
  };

  if (!showPrompt) return null;

  /*
   * FACEBOOK / INSTAGRAM BROWSER
   */
  if (isFacebookBrowser) {
    return (
      <div className="ip-overlay">
        <div
          className="ip-modal"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="ip-modal-icon">
            <img
              src="/icons/icon-96x96.png"
              alt="Godly Match"
            />
          </div>

          <h3 className="ip-modal-title">
            Install Godly Match
          </h3>

          <p className="ip-modal-sub">
            You're currently viewing Godly Match inside Facebook.
          </p>

          <p className="ip-modal-sub">
            To install Godly Match on your phone, open this page
            in <strong>Chrome</strong> or <strong>any external browser</strong>.
          </p>

          <div
            style={{
              background: '#f5f5f5',
              borderRadius: 12,
              padding: 14,
              marginTop: 15,
              marginBottom: 15,
              textAlign: 'left'
            }}
          >
            <strong>How to open it:</strong>

            <ol
              style={{
                marginTop: 8,
                paddingLeft: 20
              }}
            >
              <li>Tap the <strong>⋯</strong> menu in Facebook.</li>
              <li>
                Select <strong>Open in browser</strong>.
              </li>
              <li>
                Choose <strong>Chrome</strong> or
                <strong> any external browser</strong>.
              </li>
              <li>
                Then tap <strong>Install</strong>.
              </li>
            </ol>
          </div>

          <button
            className="ip-btn-primary"
            style={{
              width: '100%',
              padding: 12
            }}
            onClick={handleDismiss}
          >
            Got it
          </button>
        </div>
      </div>
    );
  }

  /*
   * iOS INSTALL INSTRUCTIONS
   */
  if (showIOSSteps) {
    return (
      <div
        className="ip-overlay"
        onClick={handleDismiss}
      >
        <div
          className="ip-modal"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="ip-modal-icon">
            <img
              src="/icons/icon-96x96.png"
              alt="Godly Match"
            />
          </div>

          <h3 className="ip-modal-title">
            Install Godly Match
          </h3>

          <p className="ip-modal-sub">
            Tap the{' '}
            <span className="ip-badge">
              ⬆ Share
            </span>{' '}
            button at the bottom of Safari, then scroll down
            and tap{' '}
            <span className="ip-badge">
              Add to Home Screen
            </span>
          </p>

          <button
            className="ip-btn-primary"
            style={{
              width: '100%',
              padding: 12
            }}
            onClick={handleDismiss}
          >
            Got it
          </button>
        </div>
      </div>
    );
  }

  /*
   * NORMAL CHROME / SAMSUNG INTERNET INSTALL PROMPT
   */
  return (
    <div className="ip-banner">
      <div className="ip-banner-left">
        <img
          className="ip-banner-icon"
          src="/icons/icon-96x96.png"
          alt="Godly Match"
        />

        <div className="ip-banner-text">
          <strong>Get the full app</strong>

          <span>
            Install for faster access &amp; notifications
          </span>
        </div>
      </div>

      <div className="ip-banner-actions">
        <button
          className="ip-btn-ghost"
          onClick={handleDismiss}
        >
          Not now
        </button>

        <button
          className="ip-btn-primary"
          onClick={handleInstall}
        >
          Install
        </button>
      </div>
    </div>
  );
}